import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, Search, ChevronDown, ChevronRight, Maximize2, Minimize2,
  X, Layout, Monitor, PanelLeftClose, PanelLeft, Wifi, WifiOff,
  RotateCcw, Volume2, VolumeX, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import videoWallTutorial from '../components/tutorials/configs/videoWallTutorial';
import useAuthStore from '../stores/authStore';
import useVideoWallStore from '../stores/videoWallStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import WebRTCPlayer from '../components/WebRTCPlayer';
import DigitalZoom from '../components/DigitalZoom';
import MilestonePlayer from '../components/MilestonePlayer';
import GenetecPlayer from '../components/GenetecPlayer';

const isMilestone = (cam) => cam?.vmsType === 'MILESTONE_VMS' || cam?.metadata?.vmsType === 'milestone';
const isGenetec = (cam) => cam?.integrationType === 'GENETEC_VMS' || cam?.vmsType === 'GENETEC_VMS';

// Layout presets
const LAYOUT_PRESETS = {
  '1x1': { cols: 1, rows: 1, label: '1×1', cells: 1 },
  '2x2': { cols: 2, rows: 2, label: '2×2', cells: 4 },
  '1+5': { cols: 3, rows: 2, label: '1+5', cells: 6 },
  '2x3': { cols: 3, rows: 2, label: '2×3', cells: 6 },
  '2x4': { cols: 4, rows: 2, label: '2×4', cells: 8 },
  '3x3': { cols: 3, rows: 3, label: '3×3', cells: 9 },
  '4x4': { cols: 4, rows: 4, label: '4×4', cells: 16 },
};

// Stream stagger delay
const STREAM_STAGGER_MS = 400;

/* ───────── Camera Sidebar ───────── */
const CameraSidebar = () => {
  const {
    cameras, camerasLoading, sidebarSearch, setSidebarSearch,
    cells, assignCamera, sidebarOpen, setSidebarOpen
  } = useVideoWallStore();
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const placedIds = new Set(Object.values(cells).map(c => c.id));

  // Group cameras by VMS type
  const grouped = cameras.reduce((acc, cam) => {
    const group = cam.vmsType || cam.source || 'Wave VMS';
    if (!acc[group]) acc[group] = [];
    acc[group].push(cam);
    return acc;
  }, {});

  const filteredGroups = Object.entries(grouped)
    .map(([vms, cams]) => [
      vms,
      cams.filter(c =>
        c.name.toLowerCase().includes(sidebarSearch.toLowerCase())
      )
    ])
    .filter(([, cams]) => cams.length > 0);

  const toggleGroup = (vms) => {
    setCollapsedGroups(prev => ({ ...prev, [vms]: !prev[vms] }));
  };

  const handleDragStart = (e, camera) => {
    if (placedIds.has(camera.id)) return;
    e.dataTransfer.setData('application/json', JSON.stringify(camera));
    e.dataTransfer.effectAllowed = 'copy';
  };

  if (!sidebarOpen) {
    return (
      <div className="vw-sidebar-collapsed">
        <button className="vw-sidebar-toggle" onClick={() => setSidebarOpen(true)}>
          <PanelLeft size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="vw-sidebar">
      <div className="vw-sidebar-header">
        <span className="vw-sidebar-title">Cameras</span>
        <button className="vw-sidebar-toggle" onClick={() => setSidebarOpen(false)}>
          <PanelLeftClose size={18} />
        </button>
      </div>

      <div className="vw-sidebar-search">
        <Search size={14} className="vw-search-icon" />
        <input
          type="text"
          placeholder="Search cameras..."
          value={sidebarSearch}
          onChange={(e) => setSidebarSearch(e.target.value)}
        />
      </div>

      <div className="vw-sidebar-list">
        {camerasLoading ? (
          <div className="vw-sidebar-loading">
            <div className="loading-spinner" />
            <span>Loading cameras...</span>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="vw-sidebar-empty">No cameras found</div>
        ) : (
          filteredGroups.map(([vms, cams]) => (
            <div key={vms} className="vw-camera-group">
              <button
                className="vw-group-header"
                onClick={() => toggleGroup(vms)}
              >
                {collapsedGroups[vms] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                <span>{vms}</span>
                <span className="vw-group-count">{cams.length}</span>
              </button>

              {!collapsedGroups[vms] && cams.map(cam => {
                const placed = placedIds.has(cam.id);
                const isOnline = cam.status === 'online' || cam.status === 'online' || cam.status === 'Online' || cam.status === 'Recording';

                return (
                  <div
                    key={cam.id}
                    className={`vw-camera-item ${placed ? 'placed' : ''} ${!isOnline ? 'offline' : ''}`}
                    draggable={!placed}
                    onDragStart={(e) => handleDragStart(e, cam)}
                    title={placed ? 'Already on wall' : `Drag to add: ${cam.name}`}
                  >
                    <div className={`vw-camera-status ${isOnline ? 'online' : 'offline'}`} />
                    <span className="vw-camera-name">{cam.name}</span>
                    {placed && <span className="vw-camera-placed">●</span>}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      <div className="vw-sidebar-footer">
        {Object.keys(cells).length} cameras on wall
      </div>
    </div>
  );
};

/* ───────── Video Cell ───────── */
const VideoCell = ({ cellIndex, camera, staggerDelay }) => {
  const { removeCamera, setExpandedCell } = useVideoWallStore();
  const streamQuality = useVideoWallStore(s => s.getStreamQuality());
  const [showStream, setShowStream] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [fallback, setFallback] = useState(false);
  const [genetecConfig, setGenetecConfig] = useState(null);
  const controlsTimeout = useRef(null);

  // Fetch Genetec stream config
  useEffect(() => {
    if (!camera || !isGenetec(camera)) return;
    let cancelled = false;
    fetchWithTenant('/api/vms/genetec/' + camera.integrationId + '/stream-config/' + encodeURIComponent(camera.id))
      .then(r => r.json())
      .then(config => { if (!cancelled) setGenetecConfig(config); })
      .catch(err => console.error('[VideoWall] Genetec config error:', err));
    return () => { cancelled = true; };
  }, [camera?.id]);

  // Load thumbnail once (stop polling once stream is live)
  useEffect(() => {
    if (!camera || camera.thumbnailSupported === false) return;
    let cancelled = false;
    const loadThumb = async () => {
      try {
        const res = await fetchWithTenant(`/api/vms/cameras/${encodeURIComponent(camera.id)}/thumbnail`);
        if (res.ok && !cancelled) {
          const blob = await res.blob();
          setThumbnailUrl(URL.createObjectURL(blob));
        }
      } catch (e) {
        // Thumbnail not critical
      }
    };
    loadThumb();
    return () => {
      cancelled = true;
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
    };
  }, [camera?.id]);

  // Staggered stream start (only once per camera)
  const streamStartedRef = useRef(false);
  useEffect(() => {
    if (!camera || streamStartedRef.current) return;
    streamStartedRef.current = true;
    const timer = setTimeout(() => setShowStream(true), staggerDelay);
    return () => {
      clearTimeout(timer);
      streamStartedRef.current = false;
      setShowStream(false);
      setFallback(false);
    };
  }, [camera?.id]);

  const handleMouseEnter = () => {
    setShowControls(true);
    clearTimeout(controlsTimeout.current);
  };

  const handleMouseLeave = () => {
    controlsTimeout.current = setTimeout(() => setShowControls(false), 1000);
  };

  if (!camera) return null;

  return (
    <div
      className="vw-cell-content"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={() => setExpandedCell(cellIndex)}
    >
      {/* Thumbnail background (always present) */}
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          className="vw-cell-thumbnail"
          alt=""
          style={{ display: (!showStream || fallback) ? 'block' : 'none' }}
        />
      )}

      {/* WebRTC stream */}
      {showStream && !fallback && (
        <div className="vw-cell-stream">
          {isGenetec(camera) && genetecConfig ? (
            <GenetecPlayer
              cameraGuid={genetecConfig.cameraGuid}
              integrationId={genetecConfig.integrationId}
              mediaGatewayEndpoint={genetecConfig.mediaGatewayEndpoint}
              gwpJsUrl={genetecConfig.gwpJsUrl}
              tokenEndpoint={genetecConfig.tokenEndpoint}
              autoPlay={true}
              isLiveMode={true}
              muted={true}
              onError={(msg) => { setFallback(true); console.log('[VideoWall]', camera.name, 'Genetec error:', msg); }}
            />
          ) : isMilestone(camera) ? (
            <MilestonePlayer
              camera={camera}
              integrationId={camera.integrationId || camera.metadata?.integrationId}
              onFallback={() => { setFallback(true); console.log('[VideoWall]', camera.name, 'fell back to thumbnail'); }}
            />
          ) : (
            <WebRTCPlayer
              camera={camera}
              stream="secondary"
              positionMs={null}
              muted={true}
              onFallback={() => { setFallback(true); console.log('[VideoWall]', camera.name, 'fell back to thumbnail'); }}
            />
          )}
        </div>
      )}

      {/* Loading state */}
      {!showStream && !thumbnailUrl && (
        <div className="vw-cell-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {/* Controls overlay */}
      <div className={`vw-cell-overlay-top ${showControls ? 'visible' : ''}`}>
        <span className="vw-cell-name">{camera.name}</span>
        <div className="vw-cell-actions">
          <button
            className="vw-cell-btn"
            onClick={(e) => { e.stopPropagation(); setExpandedCell(cellIndex); }}
            title="Fullscreen"
          >
            <Maximize2 size={14} />
          </button>
          <button
            className="vw-cell-btn"
            onClick={(e) => { e.stopPropagation(); removeCamera(cellIndex); }}
            title="Remove"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Live indicator */}
      {showStream && !fallback && (
        <div className="vw-cell-live-badge">
          <span className="vw-live-dot" />
          LIVE
        </div>
      )}
    </div>
  );
};

/* ───────── Expanded Single View ───────── */
const ExpandedView = () => {
  const { expandedCell, cells, setExpandedCell, isLive, positionMs } = useVideoWallStore();
  const camera = cells[expandedCell];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setExpandedCell(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!camera) return null;

  return (
    <div className="vw-expanded">
      <div className="vw-expanded-header">
        <button className="vw-expanded-back" onClick={() => setExpandedCell(null)}>
          <Minimize2 size={16} />
          <span>Back to Grid</span>
        </button>
        <span className="vw-expanded-name">{camera.name}</span>
        <div className="vw-expanded-status">
          <Wifi size={14} />
          <span>Connected</span>
        </div>
      </div>
      <div className="vw-expanded-player"><DigitalZoom>
        {isGenetec(camera) ? (
          <GenetecPlayerExpanded camera={camera} isLive={isLive} positionMs={positionMs} />
        ) : isMilestone(camera) ? (
          <MilestonePlayer
            camera={camera}
            integrationId={camera.integrationId || camera.metadata?.integrationId}
          />
        ) : (
          <WebRTCPlayer
            camera={camera}
            stream="primary"
            positionMs={isLive ? null : positionMs}
            muted={false}
          />
        )}
      </DigitalZoom>
      </div>
    </div>
  );
};

/* ───────── Timeline Bar ───────── */
const TimelineBar = () => {
  const { isLive, setIsLive, positionMs, setPositionMs } = useVideoWallStore();
  const timelineRef = useRef(null);
  const [displayTime, setDisplayTime] = useState('');

  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setDisplayTime(new Date().toLocaleTimeString());
      }, 1000);
      return () => clearInterval(interval);
    } else if (positionMs) {
      setDisplayTime(new Date(positionMs).toLocaleTimeString());
    }
  }, [isLive, positionMs]);

  const goLive = () => {
    setIsLive(true);
    setPositionMs(null);
  };

  const handleTimelineClick = (e) => {
    if (!timelineRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;

    // Map click to today's timeline (0% = midnight, 100% = now)
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const currentMs = now.getTime();
    const clickMs = startOfDay + pct * (currentMs - startOfDay);

    setPositionMs(clickMs);
    setIsLive(false);
  };

  // Calculate playhead position
  const getPlayheadPct = () => {
    if (isLive) return 100;
    if (!positionMs) return 100;
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const range = now.getTime() - startOfDay;
    if (range <= 0) return 100;
    return Math.min(100, Math.max(0, ((positionMs - startOfDay) / range) * 100));
  };

  return (
    <div className="vw-timeline">
      <div className="vw-timeline-controls">
        <button
          className={`vw-live-btn ${isLive ? 'active' : ''}`}
          onClick={goLive}
        >
          <span className="vw-live-btn-dot" />
          LIVE
        </button>
      </div>

      <div className="vw-timeline-bar" ref={timelineRef} onClick={handleTimelineClick}>
        <div className="vw-timeline-track">
          {/* Time markers */}
          {[0, 25, 50, 75, 100].map(pct => {
            const now = new Date();
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
            const markerMs = startOfDay + (pct / 100) * (now.getTime() - startOfDay);
            const markerTime = new Date(markerMs);
            return (
              <span key={pct} className="vw-timeline-marker" style={{ left: `${pct}%` }}>
                {markerTime.getHours().toString().padStart(2, '0')}:{markerTime.getMinutes().toString().padStart(2, '0')}
              </span>
            );
          })}
        </div>

        {/* Playhead */}
        <div className="vw-timeline-playhead" style={{ left: `${getPlayheadPct()}%` }}>
          <div className="vw-timeline-playhead-line" />
          <div className="vw-timeline-playhead-dot" />
        </div>
      </div>

      <div className="vw-timeline-time">
        {displayTime}
      </div>
    </div>
  );
};

/* ───────── Grid Cell (extracted to avoid hooks-in-loop) ───────── */
const GridCell = ({ cellIndex, camera, onDrop }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      className={`vw-cell ${camera ? 'filled' : 'empty'} ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => { onDrop(e, cellIndex); setIsDragOver(false); }}
    >
      {camera ? (
        <VideoCell
          cellIndex={cellIndex}
          camera={camera}
          staggerDelay={cellIndex * STREAM_STAGGER_MS}
        />
      ) : (
        <div className="vw-cell-empty">
          <Video size={24} />
          <span>{isDragOver ? 'Drop to place' : 'Drag camera here'}</span>
        </div>
      )}
    </div>
  );
};

/* ───────── Main Video Wall Component ───────── */
/* ───────── Genetec Expanded Player (fetches own config) ───────── */
const GenetecPlayerExpanded = ({ camera, isLive, positionMs }) => {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    if (!camera) return;
    let cancelled = false;
    fetchWithTenant('/api/vms/genetec/' + camera.integrationId + '/stream-config/' + encodeURIComponent(camera.id))
      .then(r => r.json())
      .then(c => { if (!cancelled) setConfig(c); })
      .catch(err => console.error('[VideoWall] Genetec expanded config error:', err));
    return () => { cancelled = true; };
  }, [camera?.id]);
  if (!config) return <div className="vw-cell-loading"><div className="loading-spinner" /></div>;
  return (
    <GenetecPlayer
      cameraGuid={config.cameraGuid}
      integrationId={config.integrationId}
      mediaGatewayEndpoint={config.mediaGatewayEndpoint}
      gwpJsUrl={config.gwpJsUrl}
      tokenEndpoint={config.tokenEndpoint}
      autoPlay={true}
      isLiveMode={isLive !== false}
      playbackTime={isLive ? null : (positionMs ? new Date(positionMs) : null)}
      muted={false}
      onError={(msg) => console.error('[VideoWall] Genetec expanded error:', msg)}
    />
  );
};

const VideoWall = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(videoWallTutorial);

  const { checkAuth, isAuthenticated, isLoading } = useAuthStore();
  const {
    layoutMode, setLayoutMode, cells, assignCamera,
    expandedCell, setExpandedCell, isLive, positionMs,
    cameras, setCameras, setCamerasLoading
  } = useVideoWallStore();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  // Auth check
  useEffect(() => {
    checkAuth();
  }, []);

  // Fetch cameras (guarded)
  const camerasFetchedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || camerasFetchedRef.current) return;
    camerasFetchedRef.current = true;

    const loadCameras = async () => {
      setCamerasLoading(true);
      try {
        const res = await fetchWithTenant('/api/vms/cameras');
        if (res.ok) {
          const data = await res.json();
          const camList = data.cameras || data || []; console.log("[VideoWall] Camera data sample:", JSON.stringify(camList[0])); setCameras(camList);
        }
      } catch (err) {
        console.error('Failed to load cameras:', err);
      } finally {
        setCamerasLoading(false);
      }
    };

    loadCameras();
  }, [isAuthenticated]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && expandedCell !== null) {
        setExpandedCell(null);
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        if (!e.ctrlKey && !e.metaKey && document.activeElement?.tagName !== 'INPUT') {
          toggleFullscreen();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedCell]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Handle drop on grid cell
  const handleDrop = useCallback((e, cellIndex) => {
    e.preventDefault();
    try {
      const camera = JSON.parse(e.dataTransfer.getData('application/json'));
      if (camera && camera.id) {
        assignCamera(cellIndex, camera);
      }
    } catch (err) {
      console.error('Drop error:', err);
    }
  }, [assignCamera]);

  // Loading / auth states
  if (isLoading) {
    return (
      <div className="vw-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="vw-loading">
        <p>Not authenticated. Please log in to the main application first.</p>
      </div>
    );
  }

  const currentLayout = LAYOUT_PRESETS[layoutMode];
  const totalCells = currentLayout.cells;
  const activeStreamCount = Object.keys(cells).length;

  const isExpanded = expandedCell !== null && cells[expandedCell];

  return (
    <div className="vw-container" ref={containerRef}>
      {/* Toolbar */}
      <div className="vw-toolbar">
        <div className="vw-toolbar-left">
          {isExpanded ? (
            <button className="vw-expanded-back" onClick={() => setExpandedCell(null)}>
              <Minimize2 size={14} />
              <span>Back to Grid</span>
            </button>
          ) : (
            <>
              <div className="vw-toolbar-brand">
                <Monitor size={18} />
                <span>Video Wall</span>
              </div>
              <span className="vw-toolbar-divider" />
              <span className="vw-stream-count">
                {activeStreamCount}/{totalCells} streams
              </span>
            </>
          )}
          {isExpanded && (
            <span className="vw-expanded-name">{cells[expandedCell]?.name}</span>
          )}
        </div>

        {!isExpanded && (
          <div className="vw-toolbar-center">
            {Object.entries(LAYOUT_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                className={`vw-layout-btn ${layoutMode === key ? 'active' : ''}`}
                onClick={() => setLayoutMode(key)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}

        <div className="vw-toolbar-right">
          <button className="vw-toolbar-btn tutorial-help-btn" onClick={() => startTutorial(videoWallTutorial.module, videoWallTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
          <button className="vw-toolbar-btn" onClick={toggleFullscreen} title="Fullscreen (F)">
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* Main area - grid is ALWAYS mounted, just hidden when expanded */}
      <div className="vw-main" style={{ display: isExpanded ? 'none' : 'flex' }}>
        <CameraSidebar />
        <div className="vw-grid-container">
          <div
            className="vw-grid"
            style={{
              gridTemplateColumns: `repeat(${currentLayout.cols}, 1fr)`,
              gridTemplateRows: `repeat(${currentLayout.rows}, 1fr)`,
            }}
          >
            {Array.from({ length: totalCells }).map((_, idx) => (
              <GridCell
                key={`${layoutMode}-${idx}`}
                cellIndex={idx}
                camera={cells[idx]}
                onDrop={handleDrop}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Expanded overlay - separate player on top, grid stays alive underneath */}
      {isExpanded && (
        <div className="vw-expanded">
          <div className="vw-expanded-player"><DigitalZoom>
            {isGenetec(cells[expandedCell]) ? (
              <GenetecPlayerExpanded camera={cells[expandedCell]} isLive={isLive} positionMs={positionMs} />
            ) : isMilestone(cells[expandedCell]) ? (
              <MilestonePlayer
                camera={cells[expandedCell]}
                integrationId={cells[expandedCell]?.integrationId || cells[expandedCell]?.metadata?.integrationId}
              />
            ) : (
              <WebRTCPlayer
                camera={cells[expandedCell]}
                stream="primary"
                positionMs={isLive ? null : positionMs}
                muted={false}
              />
            )}
          </DigitalZoom>
          </div>
        </div>
      )}

      {/* Timeline */}
      <TimelineBar />

      {showPrompt && (
        <TutorialPrompt
          module={videoWallTutorial.module}
          moduleName={videoWallTutorial.moduleName}
          steps={videoWallTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default VideoWall;
